import { Alert, Box, Button, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';
import {
  ArrowBackRounded,
  ViewListRounded,
  AssignmentTurnedInRounded,
  StarRounded,
  AutoStoriesRounded,
  EventAvailableRounded,
  LockClockRounded,
  FactCheckRounded,
} from '@mui/icons-material';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSection } from '../hooks/use-portal-data';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import { deriveSectionLifecycle, formatDateEs } from '../util/lifecycle';
import { todayInZoneWeb } from '@dosfilos/shared';
import { useTenantTimeZone } from '../../../stores/tenant.store';
import { useClassroomOn } from '../hooks/use-classroom-on';
import {
  CLASSROOM_COURSE_TABS,
  coreCourseFacts,
  defaultCourseTab,
  visibleCourseTabs,
} from '../util/classroom';
import { CourseCoreFacts } from '../components/CourseCoreFacts';
import { SelfDropButton } from '../components/SelfDropButton';

const COURSE_COLORS = [
  '#6366f1',
  '#10b981',
  '#f59e0b',
  '#ec4899',
  '#0ea5e9',
  '#8b5cf6',
  '#ef4444',
  '#14b8a6',
];

export function courseColor(sectionId: string): string {
  let hash = 0;
  for (let i = 0; i < sectionId.length; i++) hash = (hash * 31 + sectionId.charCodeAt(i)) | 0;
  return COURSE_COLORS[Math.abs(hash) % COURSE_COLORS.length]!;
}

const TABS = [
  { labelKey: 'tabModules', value: 'modules', icon: <ViewListRounded fontSize="small" /> },
  { labelKey: 'tabClasses', value: 'clases', icon: <FactCheckRounded fontSize="small" /> },
  {
    labelKey: 'tabAssignments',
    value: 'assignments',
    icon: <AssignmentTurnedInRounded fontSize="small" />,
  },
  { labelKey: 'tabGrades', value: 'grades', icon: <StarRounded fontSize="small" /> },
  { labelKey: 'tabSyllabus', value: 'syllabus', icon: <AutoStoriesRounded fontSize="small" /> },
];

export function CourseShell(): JSX.Element {
  const { sectionId } = useParams<{ sectionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('sis');
  const section = useSection(sectionId);
  const todayYmd = todayInZoneWeb(useTenantTimeZone());
  // SIN AULA, el curso no muestra Módulos, Tareas ni Notas (util/classroom).
  const classroom = useClassroomOn();

  if (!classroom.settled) return <PortalLoading />;
  if (!section) {
    return (
      <PortalEmpty
        title={t('portal.courseShell.notFoundTitle')}
        body={t('portal.courseShell.notFoundBody')}
      />
    );
  }

  const color = courseColor(section.id);
  const tabs = visibleCourseTabs(TABS, classroom.on);
  // Un enlace guardado a una pestaña del aula, con el aula apagada: a Clases.
  const onHiddenTab =
    !classroom.on && [...CLASSROOM_COURSE_TABS].some((v) => location.pathname.includes(`/${v}`));
  if (onHiddenTab) {
    return <Navigate to={`/portal-v2/courses/${sectionId}/${defaultCourseTab(false)}`} replace />;
  }
  const currentTab =
    tabs.find((tab) => location.pathname.includes(`/${tab.value}`))?.value ??
    defaultCourseTab(classroom.on);
  const lifecycle = deriveSectionLifecycle(section, todayYmd);
  const isConcluded = lifecycle === 'concluded' || lifecycle === 'archived';
  const isUpcoming = lifecycle === 'upcoming';
  // A detail view (e.g. /courses/:id/assignments/:assignmentId) renders an extra
  // path segment beyond the tab. There the assignment has its own title block, so
  // the full course header is redundant — collapse it to a slim breadcrumb and
  // hide the lifecycle alerts to give the content (esp. two-pane PDF) more room.
  const isDetail = location.pathname.split('/').filter(Boolean).length > 4;
  const coreFacts = coreCourseFacts(section, classroom.on);

  return (
    <Stack spacing={2}>
      {/* Course header — compact breadcrumb on detail views, full elsewhere. */}
      {isDetail ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ borderLeft: `4px solid ${color}`, pl: 1.5, py: 0.25 }}
        >
          <Button
            size="small"
            startIcon={<ArrowBackRounded />}
            onClick={() => navigate('/portal-v2')}
            sx={{ color: '#8b949e', ml: -0.5, minWidth: 0 }}
          >
            {t('portal.courseShell.back')}
          </Button>
          <Typography variant="caption" color="#8b949e">
            ·
          </Typography>
          <Typography variant="subtitle2" fontWeight={700} color="#e6edf3" noWrap>
            {section.courseName}
          </Typography>
          <Typography variant="caption" color="#8b949e" sx={{ letterSpacing: 0.5 }}>
            {section.courseCode}
          </Typography>
        </Stack>
      ) : (
        <Box>
          <Button
            size="small"
            startIcon={<ArrowBackRounded />}
            onClick={() => navigate('/portal-v2')}
            sx={{ color: '#8b949e', mb: 0.5, ml: -0.5 }}
          >
            {t('portal.courseShell.back')}
          </Button>
          <Box
            sx={{
              borderLeft: `4px solid ${color}`,
              pl: 2,
            }}
          >
            <Typography variant="caption" color="#8b949e" sx={{ letterSpacing: 0.5 }}>
              {section.courseCode}
            </Typography>
            <Typography
              variant="h5"
              fontWeight={700}
              color="#e6edf3"
              lineHeight={1.2}
              sx={{ fontSize: { xs: 20, sm: 24 } }}
            >
              {section.courseName}
            </Typography>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              mt={1}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                size="small"
                label={section.facultyName || t('portal.courseShell.noFaculty')}
                variant="outlined"
                sx={{ borderColor: '#30363d', color: '#c9d1d9' }}
              />
              <Typography variant="caption" color="#8b949e">
                {t('portal.courseShell.credits', { count: section.credits })}
              </Typography>
              {section.programName && (
                <Typography variant="caption" color="#8b949e">
                  · {section.programName}
                </Typography>
              )}
            </Stack>
          </Box>
        </Box>
      )}

      {/* SIN AULA: lo que sabe el núcleo del curso (sección, horario, profesor
          y la nota final cuando el acta está cerrada). */}
      {!isDetail && coreFacts && <CourseCoreFacts facts={coreFacts} />}

      {!isDetail && isConcluded && (
        <Alert
          severity="info"
          icon={<LockClockRounded fontSize="small" />}
          sx={{
            bgcolor: 'rgba(139,148,158,0.12)',
            border: '1px solid #30363d',
            color: '#c9d1d9',
            '& .MuiAlert-icon': { color: '#8b949e' },
          }}
        >
          {section.endDate
            ? t('portal.courseShell.concludedWithDate', { date: formatDateEs(section.endDate) })
            : t('portal.courseShell.concluded')}
        </Alert>
      )}
      {!isDetail && isUpcoming && (
        <Alert
          severity="info"
          icon={<EventAvailableRounded fontSize="small" />}
          // H8: antes del inicio, el alumno puede darse de baja solo.
          action={
            <SelfDropButton enrollmentId={section.enrollmentId} courseName={section.courseName} />
          }
          sx={{
            bgcolor: 'rgba(99,102,241,0.12)',
            border: '1px solid rgba(99,102,241,0.4)',
            color: '#c7d2fe',
            '& .MuiAlert-icon': { color: '#a5b4fc' },
          }}
        >
          {t('portal.courseShell.upcoming', { date: formatDateEs(section.startDate) })}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid #21262d' }}>
        <Tabs
          value={currentTab}
          onChange={(_, v) => navigate(`/portal-v2/courses/${sectionId}/${v}`)}
          textColor="inherit"
          variant="scrollable"
          allowScrollButtonsMobile
          TabIndicatorProps={{ style: { backgroundColor: color } }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={t(`portal.courseShell.${tab.labelKey}`)}
              icon={tab.icon}
              iconPosition="start"
              // onChange only fires when the selected value changes; from a
              // detail route (e.g. /assignments/:id the active tab is already
              // "assignments") clicking the same tab would be a no-op. onClick
              // always fires, so the tab also acts as "back to the list".
              onClick={() => navigate(`/portal-v2/courses/${sectionId}/${tab.value}`)}
              sx={{
                color: '#8b949e',
                textTransform: 'none',
                minHeight: 48,
                '&.Mui-selected': { color: '#e6edf3' },
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Outlet />
    </Stack>
  );
}

/** Abrir un curso: a «Módulos» con aula, a «Clases» sin ella. */
export function CourseRedirectToModules(): JSX.Element {
  const { sectionId } = useParams<{ sectionId: string }>();
  const classroom = useClassroomOn();
  if (!sectionId || !classroom.settled) return <PortalLoading />;
  return (
    <Navigate to={`/portal-v2/courses/${sectionId}/${defaultCourseTab(classroom.on)}`} replace />
  );
}
